"use client";

import React, { useRef, useEffect } from "react";
import Image from "next/image";
import { Title, Description } from "@/components/Common/Typography";
import styles from "../export.module.css";

export interface CommitmentSectionProps {
  data?: {
    subtitle?: string;
    title: string;
    qcTitle?: string;
    qcDesc?: string;
    qcBullets?: string[];
    certTitle?: string;
    certDesc?: string;
    certs?: { name: string; src: string }[];
  };
}

export default function CommitmentSection({ data }: CommitmentSectionProps) {
  const certsGridRef = useRef<HTMLDivElement>(null);
  const qcListRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const enableDragScroll = (slider: HTMLElement | null) => {
      if (!slider) return;

      let isDown = false;
      let startX = 0;
      let scrollLeft = 0;

      const handleMouseDown = (e: MouseEvent) => {
        isDown = true;
        slider.style.cursor = "grabbing";
        startX = e.pageX - slider.offsetLeft;
        scrollLeft = slider.scrollLeft;
      };

      const handleMouseLeave = () => {
        isDown = false;
        slider.style.cursor = "grab";
      };

      const handleMouseUp = () => {
        isDown = false;
        slider.style.cursor = "grab";
      };

      const handleMouseMove = (e: MouseEvent) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - slider.offsetLeft;
        const walk = (x - startX) * 1.5;
        slider.scrollLeft = scrollLeft - walk;
      };

      slider.style.cursor = "grab";
      slider.addEventListener("mousedown", handleMouseDown);
      slider.addEventListener("mouseleave", handleMouseLeave);
      slider.addEventListener("mouseup", handleMouseUp);
      slider.addEventListener("mousemove", handleMouseMove);

      return () => {
        slider.removeEventListener("mousedown", handleMouseDown);
        slider.removeEventListener("mouseleave", handleMouseLeave);
        slider.removeEventListener("mouseup", handleMouseUp);
        slider.removeEventListener("mousemove", handleMouseMove);
      };
    };

    const cleanupCerts = enableDragScroll(certsGridRef.current);
    const cleanupQc = enableDragScroll(qcListRef.current);

    return () => {
      if (cleanupCerts) cleanupCerts();
      if (cleanupQc) cleanupQc();
    };
  }, []);

  if (!data || !data.title) return null;

  const defaultCerts = [
    { name: "Fairtrade", src: "/images/certs/fairtrade.jpg?v=2" },
    { name: "Rainforest Alliance", src: "/images/certs/rainforest.jpg?v=2" },
    { name: "4C Certification", src: "/images/certs/logo4c.png?v=2" },
    { name: "HACCP", src: "/images/certs/haccp.jpg?v=2" },
    { name: "FDA", src: "/images/certs/fda.jpg?v=2" },
    { name: "Halal", src: "/images/certs/halal.jpg?v=2" },
    { name: "ISO", src: "/images/certs/iso.png?v=2" },
    { name: "GACC", src: "/images/certs/gacc.jpg?v=2" },
    { name: "UTZ", src: "/images/certs/utz.png?v=2" },
  ];

  const certList =
    data.certs && data.certs.length > 0 ? data.certs : defaultCerts;

  return (
    <section className={styles.commitmentSection}>
      <div className={styles.container}>
        <div className={styles.sectionHeader}>
          <Title
            as="h2"
            size="medium"
            align="center"
            className={styles.sectionTitle}
          >
            {data.title}
          </Title>
        </div>

        <div className={styles.commitmentSplitGrid}>
          {/* LEFT: Kiểm soát chất lượng */}
          <div className={styles.commitmentCol}>
            {data.qcTitle && (
              <Title
                as="h3"
                size="small"
                className={styles.commitmentColTitle}
              >
                {data.qcTitle}
              </Title>
            )}
            {data.qcDesc && (
              <Description as="p" size="medium" className={styles.commitmentColDesc}>
                {data.qcDesc}
              </Description>
            )}
            {data.qcBullets && data.qcBullets.length > 0 && (
              <ul ref={qcListRef} className={styles.commitmentList}>
                {data.qcBullets.map((bullet, idx) => (
                  <li key={idx} className={styles.commitmentItem}>
                    <span className={styles.commitmentBulletIcon}>✓</span>
                    <Description as="span" size="medium">
                      {bullet}
                    </Description>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* VERTICAL DIVIDER */}
          <div className={styles.commitmentDivider} />

          {/* RIGHT: Chứng nhận chất lượng */}
          <div className={styles.commitmentCol}>
            {data.certTitle && (
              <Title
                as="h3"
                size="small"
                className={styles.commitmentColTitle}
              >
                {data.certTitle}
              </Title>
            )}
            {data.certDesc && (
              <Description as="p" size="medium" className={styles.commitmentColDesc}>
                {data.certDesc}
              </Description>
            )}
            <div ref={certsGridRef} className={styles.certsGrid}>
              {certList.map((cert, cIdx) => (
                <div key={cIdx} className={styles.certGridItem}>
                  <Image
                    src={cert.src}
                    alt={cert.name}
                    fill
                    sizes="80px"
                    className={styles.certGridImg}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
